import { useState } from 'react';
import { ArrowUpRight, CheckCircle2 } from 'lucide-react';
import Modal from '../ui/Modal';
import Ayuda from '../ui/Ayuda';
import { api, ErrorApi } from '../api';

/* =====================================================================
   Escalar a TEGO · DIRECTRIZ-TICKETS F6
   =====================================================================
   El modal que manda un ticket «Problema con TEGO» al soporte de TEGO
   (OPS). La regla dura es R-TK6: el operador ve SOLO lo que se escribe
   aquí. Por eso la pantalla lo dice claro y sin letra chica: lo que no
   pongas en el resumen, no sale de tu empresa. No enciende ningún modo
   soporte: solo manda el resumen.
   ===================================================================== */

export default function EscalarATego({ idTicket, onCerrar, onEscalado }) {
  const [resumen, setResumen] = useState('');
  const [incluirDescripcion, setIncluirDescripcion] = useState(false);
  const [contacto, setContacto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [referencia, setReferencia] = useState('');

  async function enviar() {
    if (!resumen.trim() || enviando) return;
    setEnviando(true);
    setError('');
    try {
      const r = await api.post(`/tickets/${idTicket}/escalar`, {
        resumen: resumen.trim(),
        incluir_descripcion: incluirDescripcion,
        contacto: contacto.trim() || null,
      });
      setReferencia(r.referencia);
      if (onEscalado) onEscalado(r);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo escalar. Revisa tu conexión e inténtalo otra vez.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Escalar a TEGO"
      subtitulo="Manda este problema al soporte de TEGO. Solo llega lo que escribas aquí."
      icono={ArrowUpRight}
      onCerrar={onCerrar}
      bloqueado={enviando}
    >
      <div className="flex flex-col gap-3 p-4">
        <Ayuda id="escalar.pantalla" />

        {referencia ? (
          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <p className="flex items-center gap-2 font-medium text-green-900">
              <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> Enviado al soporte de TEGO
            </p>
            <p className="mt-1 text-sm text-green-800">
              Tu referencia es <span className="font-semibold">{referencia}</span>.
              Guárdala para dar seguimiento.
            </p>
            <button
              type="button"
              onClick={onCerrar}
              className="mt-3 min-h-[48px] w-full rounded-xl border border-gray-300
                         font-medium text-gray-700 hover:bg-gray-50"
            >
              Listo
            </button>
          </div>
        ) : (
          <>
            {error && (
              <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>
            )}

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-900">Resumen para TEGO</span>
              <Ayuda id="escalar.resumen" />
              <textarea
                value={resumen}
                onChange={(e) => setResumen(e.target.value)}
                rows={4}
                placeholder="Cuéntale al soporte de TEGO qué está pasando…"
                className="w-full rounded-xl border border-gray-300 p-3 text-base
                           focus:border-[var(--color-marca)] focus:outline-none
                           focus:ring-2 focus:ring-[var(--color-marca)]"
              />
            </label>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={incluirDescripcion}
                onChange={(e) => setIncluirDescripcion(e.target.checked)}
                className="mt-0.5 h-5 w-5"
              />
              <span>
                Incluir la descripción de mi ticket
                <Ayuda id="escalar.incluir_descripcion" />
              </span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-900">Cómo te contactamos (opcional)</span>
              <Ayuda id="escalar.contacto" />
              <input
                type="text"
                value={contacto}
                onChange={(e) => setContacto(e.target.value)}
                placeholder="Correo o teléfono"
                className="w-full rounded-xl border border-gray-300 p-3 text-base
                           focus:border-[var(--color-marca)] focus:outline-none
                           focus:ring-2 focus:ring-[var(--color-marca)]"
              />
            </label>

            <button
              type="button"
              onClick={enviar}
              disabled={!resumen.trim() || enviando}
              className="min-h-[48px] rounded-xl bg-[var(--color-marca)] px-4 font-semibold
                         text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {enviando ? 'Enviando…' : 'Escalar a TEGO'}
            </button>
            {!resumen.trim() && (
              <p className="text-xs text-gray-500">
                Escribe el resumen para poder escalar: es lo único que verá el soporte.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
